import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const cardVariants = cva(
  [
    'rounded-card',
    'border border-[var(--border-default)]',
    'transition-all duration-200',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-[var(--surface)]',
          'hover:border-[var(--primary)]/20 hover:shadow-float',
          'dark:bg-[var(--surface)]/80 dark:backdrop-blur-sm',
        ],
        // Ревизия: предыдущая пометка здесь называла elevated «карточкой на
        // витрине» из исключения ответа 35 (второй вопрос = 2). Это неверно, и
        // оставлять так опасно — следующий проход решит, что исключение уже
        // отработано. Факты, проверенные по коду:
        //   1. variant="elevated" в проекте не передаётся НИГДЕ, работает
        //      только default из defaultVariants. Ветка ниже — мёртвая.
        //   2. <Card> вообще используется в двух местах, и оба в админке
        //      (app/admin/money, components/admin/orders/heatmap). Настоящая
        //      карточка товара живёт в features/products/product-card.tsx и
        //      правится в другой зоне — исключение ответа 35 закрывается ТАМ,
        //      а не здесь.
        //   3. shadow-lg → shadow-float это уплощение, а не «объём оставлен»;
        //      глубина тут осталась только на hover (shadow-deep).
        elevated: [
          'bg-[var(--bg-elevated)] shadow-float',
          'hover:shadow-deep',
          'dark:bg-[var(--bg-elevated)]/90 dark:backdrop-blur-md',
        ],
        interactive: [
          'bg-[var(--surface)] cursor-pointer',
          'hover:border-[var(--primary)]/30 hover:shadow-float hover:-translate-y-0.5',
          'active:scale-[0.99] active:shadow-raised',
          'dark:bg-[var(--surface)]/80 dark:backdrop-blur-sm',
        ],
        outline: [
          'bg-transparent',
          'hover:bg-[var(--surface-hover)]',
        ],
        ghost: [
          'bg-transparent border-transparent',
          'hover:bg-[var(--surface-hover)] hover:border-[var(--border-default)]',
        ],
      },
      padding: {
        none: 'p-0',
        sm: 'p-3',
        default: 'p-4',
        lg: 'p-5',
        xl: 'p-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'default',
    },
  }
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, padding }), className)}
      {...props}
    />
  )
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('text-lg font-semibold text-[var(--text-primary)] tracking-tight', className)}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-[var(--text-secondary)]', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center pt-4', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  cardVariants,
};

